import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { scrollableTableWrapStyles, scrollableTableSizing } from '../styles/scrollableTable';
import { FiTag, FiPlus, FiEdit2, FiTrash2, FiRefreshCw, FiSearch } from 'react-icons/fi';
import { supabase } from '../../config/supabase';
import { useAuthContext } from '../../hooks/useAuthContext';

const Container = styled.div`
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 16px;
  gap: 8px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 14px;
  width: 300px;
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${props => props.primary ? '#5efba9' : 'white'};
  color: ${props => props.primary ? '#1f2937' : '#1f2937'};
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 500;

  &:hover {
    background: ${props => props.primary ? '#4de8a8' : '#f3f4f6'};
    border-color: ${props => props.primary ? '#5efba9' : '#d1d5db'};
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const TableContainer = styled.div`
  ${scrollableTableWrapStyles}
`;

const Table = styled.table`
  ${scrollableTableSizing}
  border-collapse: collapse;
`;

const TableHeaderCell = styled.th`
  padding: 16px;
  text-align: left;
  font-weight: 600;
  font-size: 14px;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1f2937;
  border-bottom: 1px solid #e5e7eb;
`;

const TableRow = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }

  &:last-child ${TableCell} {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.active ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.active ? '#065f46' : '#991b1b'};
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  margin-right: 8px;
  transition: all 0.2s;

  &.edit {
    background: #5efba9;
    color: #1f2937;

    &:hover {
      background: #4de8a8;
    }
  }

  &.delete {
    background: #fee2e2;
    color: #991b1b;

    &:hover {
      background: #fecaca;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;

  &:hover {
    color: #1f2937;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const Textarea = styled.textarea`
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #5efba9;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &.primary {
    background: #5efba9;
    color: #1f2937;
    border: none;

    &:hover {
      background: #4de8a8;
    }
  }

  &.secondary {
    background: white;
    color: #6b7280;
    border: 1px solid #e5e7eb;

    &:hover {
      background: #f9fafb;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 32px;
  color: #6b7280;
`;

function AdminVoucherCodes() {
  const { session } = useAuthContext();
  const [vouchers, setVouchers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    brand_id: '',
    code: '',
    description: '',
    vtype: 'percent',
    amount: '',
    currency: 'OMR',
    percent_cap: '',
    min_subtotal: '0',
    max_total_uses: '',
    max_uses_per_user: '1',
    start_at: '',
    end_at: '',
    enabled: true
  });

  useEffect(() => {
    if (session) {
      fetchBrands();
      fetchVouchers();
    }
  }, [session]);

  const fetchBrands = async () => {
    try {
      const { data, error } = await supabase
        .from('brands')
        .select('id, name')
        .eq('active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setBrands(data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      alert('Failed to fetch brands');
    }
  };

  const fetchVouchers = async () => {
    if (!session) return;

    try {
      setLoading(true);

      // Fetch all vouchers with their brand associations
      const { data, error } = await supabase
        .from('voucher_codes')
        .select(`
          *,
          voucher_brands(
            brand_id,
            brands(id, name)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Transform data to include brand info
      const vouchersWithBrands = (data || []).map(voucher => ({
        ...voucher,
        brands: voucher.voucher_brands?.map(vb => vb.brands).filter(Boolean) || []
      }));
      
      setVouchers(vouchersWithBrands);
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      alert('Failed to fetch voucher codes');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!session) return;

    if (!formData.brand_id) {
      alert('Please select a brand');
      return;
    }

    try {
      const dataToSubmit = {
        code: formData.code.trim().toUpperCase(),
        enabled: formData.enabled !== undefined ? formData.enabled : true,
        vtype: formData.vtype || 'percent',
        amount: parseFloat(formData.amount),
        currency: formData.currency || 'OMR',
        percent_cap: formData.percent_cap ? parseFloat(formData.percent_cap) : null,
        min_subtotal: parseFloat(formData.min_subtotal || 0),
        max_total_uses: formData.max_total_uses ? parseInt(formData.max_total_uses) : null,
        max_uses_per_user: parseInt(formData.max_uses_per_user || 1),
        applies_to: 'brands',
        start_at: formData.start_at || null,
        end_at: formData.end_at || null
      };

      let savedVoucher;

      if (editingId) {
        // Update voucher
        const { data, error: updateError } = await supabase
          .from('voucher_codes')
          .update(dataToSubmit)
          .eq('id', editingId)
          .select()
          .single();

        if (updateError) throw updateError;
        savedVoucher = data;

        // Update brand association - delete old and add new
        await supabase
          .from('voucher_brands')
          .delete()
          .eq('voucher_id', editingId);
      } else {
        // Create voucher
        const { data, error: insertError } = await supabase
          .from('voucher_codes')
          .insert([dataToSubmit])
          .select()
          .single();

        if (insertError) throw insertError;
        savedVoucher = data;
      }

      // Link voucher to selected brand
      const { error: vbError } = await supabase
        .from('voucher_brands')
        .upsert([
          { voucher_id: savedVoucher.id, brand_id: formData.brand_id }
        ]);

      if (vbError) throw vbError;

      setShowModal(false);
      setEditingId(null);
      resetForm();
      fetchVouchers();
    } catch (error) {
      console.error('Error saving voucher:', error);
      alert('Failed to save voucher code: ' + (error.message || 'Unknown error'));
    }
  };

  const handleEdit = async (voucher) => {
    setEditingId(voucher.id);

    // Get brand_id from voucher_brands
    let brandId = '';
    if (voucher.brands && voucher.brands.length > 0) {
      brandId = voucher.brands[0].id;
    } else {
      // Fetch brand association if not included
      const { data: vbData } = await supabase
        .from('voucher_brands')
        .select('brand_id')
        .eq('voucher_id', voucher.id)
        .limit(1)
        .single();
      
      if (vbData) {
        brandId = vbData.brand_id;
      }
    }

    setFormData({
      brand_id: brandId,
      code: voucher.code,
      enabled: voucher.enabled,
      vtype: voucher.vtype,
      amount: voucher.amount.toString(),
      currency: voucher.currency,
      percent_cap: voucher.percent_cap ? voucher.percent_cap.toString() : '',
      min_subtotal: voucher.min_subtotal?.toString() || '0',
      max_total_uses: voucher.max_total_uses ? voucher.max_total_uses.toString() : '',
      max_uses_per_user: (voucher.max_uses_per_user || 1).toString(),
      start_at: voucher.start_at ? new Date(voucher.start_at).toISOString().split('T')[0] : '',
      end_at: voucher.end_at ? new Date(voucher.end_at).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    // Use window.confirm() to avoid ESLint error
    if (!window.confirm('Are you sure you want to delete this voucher code?')) return;

    try {
      const { error } = await supabase
        .from('voucher_codes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchVouchers();
    } catch (error) {
      console.error('Error deleting voucher:', error);
      alert('Failed to delete voucher code');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      brand_id: '',
      code: '',
      enabled: true,
      vtype: 'percent',
      amount: '',
      currency: 'OMR',
      percent_cap: '',
      min_subtotal: '0',
      max_total_uses: '',
      max_uses_per_user: '1',
      start_at: '',
      end_at: ''
    });
  };

  const filteredVouchers = vouchers.filter(voucher => {
    const term = searchTerm.toLowerCase();
    const codeMatch = voucher.code.toLowerCase().includes(term);
    const brandMatch = voucher.brands?.some(brand => 
      brand.name.toLowerCase().includes(term)
    ) || false;
    return codeMatch || brandMatch;
  });

  return (
    <Container>
      <Header>
        <Title>
          <FiTag /> Voucher Codes
        </Title>
        <HeaderActions>
          <SearchBar>
            <FiSearch />
            <SearchInput
              type="text"
              placeholder="Search vouchers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <IconButton onClick={() => setShowModal(true)} primary>
            <FiPlus /> Add Voucher Code
          </IconButton>
          <IconButton onClick={fetchVouchers}>
            <FiRefreshCw /> Refresh
          </IconButton>
        </HeaderActions>
      </Header>

      <Card>
        <TableContainer>
          <Table>
            <thead>
              <TableRow>
                <TableHeaderCell>Code</TableHeaderCell>
                <TableHeaderCell>Brand</TableHeaderCell>
                <TableHeaderCell>Discount</TableHeaderCell>
                <TableHeaderCell>Usage</TableHeaderCell>
                <TableHeaderCell>Valid Until</TableHeaderCell>
                <TableHeaderCell>Status</TableHeaderCell>
                <TableHeaderCell>Actions</TableHeaderCell>
              </TableRow>
            </thead>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredVouchers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ textAlign: 'center', padding: '64px' }}>
                    <EmptyState>No voucher codes found</EmptyState>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVouchers.map(voucher => (
                  <TableRow key={voucher.id}>
                    <TableCell><strong>{voucher.code}</strong></TableCell>
                    <TableCell>
                      {voucher.brands && voucher.brands.length > 0 ? (
                        voucher.brands.map(brand => brand.name).join(', ')
                      ) : 'No brand'}
                    </TableCell>
                    <TableCell>
                      {voucher.amount}
                      {voucher.vtype === 'percent' ? '%' : ` ${voucher.currency}`}
                      {voucher.percent_cap && voucher.vtype === 'percent' && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          ({'max'} {voucher.percent_cap} {voucher.currency})
                        </div>
                      )}
                      {voucher.min_subtotal > 0 && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {'min'} {voucher.min_subtotal} {voucher.currency}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>

                      <div>{voucher.total_uses || 0}</div>
                      {voucher.max_total_uses && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          / {voucher.max_total_uses} {'max'}
                        </div>
                      )}
                      {voucher.max_uses_per_user && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {voucher.max_uses_per_user} {'per user'}
                        </div>
                      )}

                    </TableCell>
                    <TableCell>
                      {voucher.end_at ? new Date(voucher.end_at).toLocaleDateString() : 'No expiry'}
                      {voucher.start_at && (
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          From: {new Date(voucher.start_at).toLocaleDateString()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge active={voucher.enabled}>
                        {voucher.enabled ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <ActionButton className="edit" onClick={() => handleEdit(voucher)}>
                        <FiEdit2 />
                      </ActionButton>
                      <ActionButton className="delete" onClick={() => handleDelete(voucher.id)}>
                        <FiTrash2 />
                      </ActionButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </TableContainer>
      </Card>

      {showModal && (
        <Modal onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>{editingId ? 'Edit Voucher Code' : 'Add Voucher Code'}</ModalTitle>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>Brand *</Label>
                <Select
                  name="brand_id"
                  value={formData.brand_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a brand</option>
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Code *</Label>
                <Input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g. SAVE10"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Discount Type *</Label>
                  <Select
                    name="vtype"
                    value={formData.vtype}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="percent">Percentage (%)</option>
                    <option value="fixed">Fixed Amount</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Discount Value *</Label>
                  <Input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    required
                    step="0.01"
                    min="0"
                    max={formData.vtype === 'percent' ? '100' : undefined}
                    placeholder={formData.vtype === 'percent' ? '10' : '5'}
                  />
                </FormGroup>
              </FormRow>

              {formData.vtype === 'percent' && (
                <FormGroup>
                  <Label>Maximum Discount Cap (Optional)</Label>
                  <Input
                    type="number"
                    name="percent_cap"
                    value={formData.percent_cap}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    placeholder="e.g. 50 OMR"
                  />
                </FormGroup>
              )}

              <FormRow>
                <FormGroup>
                  <Label>Currency</Label>
                  <Select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                  >
                    <option value="OMR">OMR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </Select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <Label>Minimum Subtotal</Label>
                <Input
                  type="number"
                  name="min_subtotal"
                  value={formData.min_subtotal}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="0"
                />
              </FormGroup>

              <FormRow>
                <FormGroup>
                  <Label>Max Total Uses (0 = unlimited)</Label>
                  <Input
                    type="number"
                    name="max_total_uses"
                    value={formData.max_total_uses}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="0"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Max Uses Per User</Label>
                  <Input
                    type="number"
                    name="max_uses_per_user"
                    value={formData.max_uses_per_user}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="1"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <Label>Valid From (optional)</Label>
                  <Input
                    type="date"
                    name="start_at"
                    value={formData.start_at}
                    onChange={handleInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Valid Until (optional)</Label>
                  <Input
                    type="date"
                    name="end_at"
                    value={formData.end_at}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormRow>

              <CheckboxLabel>
                <Input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleInputChange}
                />
                Enabled
              </CheckboxLabel>

              <ButtonGroup>
                <Button type="button" className="secondary" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button type="submit" className="primary">
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </ButtonGroup>
            </Form>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
}
export default AdminVoucherCodes;

